// src/lib/cabinetExport.js
// Export d'un cabinet en image (1920 × 1080) sur un canevas. Les images ne sont jamais étirées.
import { themeById } from "./cabinetService";

const loadImage = (src) => new Promise((resolve) => {
  if (!src) return resolve(null);
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

function wrap(ctx, text, maxWidth, maxLines) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const w of words) {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > maxWidth && line) { lines.push(line); line = w; } else line = t;
  }
  if (line) lines.push(line);
  return lines.slice(0, maxLines);
}

// entries : [{ src, title, artist, caption }]. Retourne { blob, missing } (missing = images illisibles).
export async function renderCabinetPng({ title, themeId, entries }) {
  const W = 1920, H = 1080, theme = themeById(themeId);
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = theme.wall; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = theme.ink; ctx.font = "italic 44px Georgia, serif"; ctx.textBaseline = "alphabetic";
  ctx.fillText(title || "", 80, 100);

  const images = await Promise.all(entries.map((e) => loadImage(e.src)));
  const n = Math.max(entries.length, 1);
  const cols = Math.min(n, Math.ceil(Math.sqrt(n * (W / H) * 0.9)));
  const rows = Math.ceil(n / cols);
  const top = 150, bottom = 60, side = 80, gap = 40;
  const cellW = (W - side * 2 - gap * (cols - 1)) / cols;
  const cellH = (H - top - bottom - gap * (rows - 1)) / rows;
  const textH = 70;
  let missing = 0;

  entries.forEach((e, i) => {
    const c = i % cols, r = Math.floor(i / cols);
    const x = side + c * (cellW + gap), y = top + r * (cellH + gap);
    const boxW = cellW - 16, boxH = cellH - textH - 16;
    const img = images[i];
    if (img) {
      const s = Math.min(boxW / img.width, boxH / img.height);
      const w = img.width * s, h = img.height * s;
      const ix = x + (cellW - w) / 2, iy = y + (boxH - h) / 2 + 8;
      ctx.shadowColor = "rgba(0,0,0,.4)"; ctx.shadowBlur = 18; ctx.shadowOffsetY = 6;
      ctx.fillStyle = theme.frame; ctx.fillRect(ix - 6, iy - 6, w + 12, h + 12);
      ctx.shadowColor = "transparent";
      ctx.drawImage(img, ix, iy, w, h);
    } else {
      missing += 1;
      ctx.strokeStyle = theme.frame; ctx.strokeRect(x + 8, y + 8, boxW, boxH);
    }
    ctx.fillStyle = theme.ink; ctx.textAlign = "center";
    ctx.font = "italic 20px Georgia, serif";
    const head = [e.title, e.artist].filter(Boolean).join(" — ");
    ctx.fillText(wrap(ctx, head, cellW, 1)[0] || "", x + cellW / 2, y + boxH + 36);
    if (e.caption) {
      ctx.font = "15px system-ui, sans-serif"; ctx.globalAlpha = 0.85;
      wrap(ctx, e.caption, cellW, 2).forEach((l, k) => ctx.fillText(l, x + cellW / 2, y + boxH + 58 + k * 18));
      ctx.globalAlpha = 1;
    }
    ctx.textAlign = "left";
  });

  const blob = await new Promise((resolve) => {
    try { canvas.toBlob((b) => resolve(b), "image/png"); } catch { resolve(null); }
  });
  return { blob, missing };
}
